import React, {useEffect, useState, useMemo, useCallback} from 'react'
import LoaderComponent from '../../components/LoaderComponent'
import {useParams} from 'react-router-dom'
import {API_ENDPOINTS} from '../../../../constants/apiConstants'
import axios from 'axios'
import {DeviceModel, DoctorAllocatedDeviceModel, OwnershipType} from '../../../../entities/device.entity'
import {ApiResponseModel} from '../../../../entities/entities'
import {toast} from 'react-toastify'
import AssignBulkDevicesDoctorModal from '../../components/modals/AssignBulkDevicesDoctorModal'
import MetaTags from '../../../../meta/MetaTags'
import PrimaryButton from '../../components/common/buttons/PrimaryButton'
import {formatBillingCycleDateToUser, formatUTCToUserDate} from '../../helper/helperFunction'

interface DoctorProfileDevicesProps {
  isLoading: boolean
}

type AssignedFilter = 'all' | 'assigned' | 'unassigned'
type OwnershipFilter = 'all' | 'rental' | 'purchased' | 'loaned'

const DoctorProfileDevicesPage: React.FC<DoctorProfileDevicesProps> = ({isLoading}) => {
  const {doctorId} = useParams()
  const [assignedDevices, setAssignedDevices] = useState<DoctorAllocatedDeviceModel[]>([])
  const [isAssignBulkDeviceModalOpen, setIsAssignBulkDeviceModalOpen] = useState(false)
  const [availableDevices, setAvailableDevices] = useState<DeviceModel[]>([])
  const [isAssigningDevices, setIsAssigningDevices] = useState(false)
  const [unassigningDeviceId, setUnassigningDeviceId] = useState<number | null>(null)
  const [assignedFilter, setAssignedFilter] = useState<AssignedFilter>('all')
  const [ownershipFilter, setOwnershipFilter] = useState<OwnershipFilter>('all')

  const fetchAssignedDevices = useCallback(async () => {
    try {
      const response = await axios.get(API_ENDPOINTS.GET_ALL_DOCTOR_DEVICES(doctorId!, 'isUuid=1'))
      setAssignedDevices(response.data?.data?.devices || [])
    } catch (error) {
      //TODO Add Error Component
    }
  }, [doctorId])

  const fetchAvailableDevices = useCallback(async () => {
    try {
      const response = await axios.get<ApiResponseModel<{devices: DeviceModel[]}>>(
        API_ENDPOINTS.GET_ALL_AVAILABLE_DEVICES()
      )

      setAvailableDevices(response.data?.data?.devices || [])
    } catch (error) {
      //TODO Add Error Component
    }
  }, [])

  useEffect(() => {
    fetchAssignedDevices()
    fetchAvailableDevices()
  }, [fetchAssignedDevices, fetchAvailableDevices])

  const filteredDevices = useMemo(() => {
    return assignedDevices.filter((device) => {
      // Filter by assigned status
      if (assignedFilter === 'assigned' && !device.assigned_to_patient) return false
      if (assignedFilter === 'unassigned' && device.assigned_to_patient) return false

      // Filter by ownership type
      if (ownershipFilter !== 'all' && device.ownership_type !== ownershipFilter) return false

      return true
    })
  }, [assignedDevices, assignedFilter, ownershipFilter])

  const assignAvailableDevices = async (
    devices: DeviceModel[],
    ownershipType: OwnershipType,
    resetForm: () => void
  ) => {
    try {
      setIsAssigningDevices(true)
      const devicesIds = devices.map((device) => device?.id)

      const assignDeviceBody = {
        all_devices_id: devicesIds,
        doctor_uuid: doctorId,
        ownership_type: ownershipType,
      }

      const response = await axios.post<ApiResponseModel<any>>(
        API_ENDPOINTS.ASSIGN_MULTIPLE_DEVICES_TO_DOCTOR(),
        assignDeviceBody
      )
      
      // Handle success - check for status field or assume success if response is 2xx
      if (response.data.status === 'Success' || (response.status >= 200 && response.status < 300)) {
        toast.success(response.data.message || 'Devices assigned successfully')

        // Reload assigned and available devices after assignment
        await fetchAssignedDevices() //TODO Please remove this later :)
        await fetchAvailableDevices() //TODO Please remove this later :)

        resetForm()
        setIsAssignBulkDeviceModalOpen(false)
      } else {
        toast.error(response.data.message || 'Failed to assign device.')
      }
    } catch (error) {
      if (axios.isAxiosError(error)) {
        toast.error(error.response?.data?.message || 'Something went wrong')
      } else if (error instanceof Error) {
        toast.error(error.message)
      } else {
        toast.error('An unexpected error occurred')
      }
    } finally {
      setIsAssigningDevices(false)
    }
  }

  const unassignDevice = async (deviceId: number) => {
    try {
      setUnassigningDeviceId(deviceId)
      const response = await axios.put<ApiResponseModel<any>>(
        API_ENDPOINTS.UNASSIGN_DEVICE_FROM_DOCTOR(deviceId)
      )

      if (response.data.status === 'Success') {
        toast.success(response.data.message)

        // Refresh the assigned and available devices
        await fetchAssignedDevices()
        await fetchAvailableDevices()
      } else {
        toast.error(response.data.message || 'Failed to unassign device.')
      }
    } catch (error) {
      if (axios.isAxiosError(error)) {
        toast.error(error.response?.data?.message || 'Something went wrong')
      } else if (error instanceof Error) {
        toast.error(error.message)
      } else {
        toast.error('An unexpected error occurred')
      }
    } finally {
      setUnassigningDeviceId(null)
    }
  }

  if (isLoading) {
    return <LoaderComponent />
  }

  return (
    <>
      <MetaTags title='Doctor Devices' />
      <div className='card'>
        <div className='card-header border-0 pt-6'>
          <div className='card-title'>
            <h3 className='fw-bolder m-0'>Doctor Devices</h3>
          </div>
          <PrimaryButton
            className=''
            onClick={() => setIsAssignBulkDeviceModalOpen(true)}
            disabled={isAssigningDevices}
          >
            {isAssigningDevices ? (
              <span className='d-flex align-items-center'>
                <span className='spinner-border spinner-border-sm me-2' aria-hidden='true'></span>
                Assigning...
              </span>
            ) : (
              'Assign Device'
            )}
          </PrimaryButton>
        </div>

        {/* Filters */}
        <div className='card-body py-4 border-bottom'>
          <div className='d-flex gap-4 flex-wrap'>
            <div className='d-flex align-items-center gap-2'>
              <label className='fw-semibold text-muted'>Patient Status:</label>
              <select
                className='form-select form-select-sm w-auto'
                value={assignedFilter}
                onChange={(e) => setAssignedFilter(e.target.value as AssignedFilter)}
              >
                <option value='all'>All</option>
                <option value='assigned'>Assigned to Patient</option>
                <option value='unassigned'>Not Assigned</option>
              </select>
            </div>
            <div className='d-flex align-items-center gap-2'>
              <label className='fw-semibold text-muted'>Ownership:</label>
              <select
                className='form-select form-select-sm w-auto'
                value={ownershipFilter}
                onChange={(e) => setOwnershipFilter(e.target.value as OwnershipFilter)}
              >
                <option value='all'>All</option>
                <option value='rental'>Rental</option>
                <option value='purchased'>Purchased</option>
                <option value='loaned'>Loaned</option>
              </select>
            </div>
            <div className='d-flex align-items-center'>
              <span className='text-muted'>
                Showing {filteredDevices.length} of {assignedDevices.length} devices
              </span>
            </div>
          </div>
        </div>

        {/* Table View */}
        <div className='card-body p-0'>
          {assignedDevices.length > 0 ? (
            <div className='table-responsive'>
              <table className='table table-row-bordered table-row-gray-100 align-middle gs-0 gy-3 mb-0'>
                <thead>
                  <tr className='fw-bold text-muted bg-light'>
                    <th className='ps-4'>Device</th>
                    <th className='d-none d-md-table-cell'>IMEI</th>
                    <th>Ownership</th>
                    <th>Patient</th>
                    <th className='d-none d-lg-table-cell'>Patient Assigned</th>
                    <th className='d-none d-lg-table-cell'>Doctor Assigned</th>
                    <th className='text-end pe-4'>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredDevices.length > 0 ? (
                    filteredDevices.map((device) => {
                      const patient = device?.Device?.AllocatedDevice?.[0]?.Patient
                      const patientAssignedDate = device?.Device?.AllocatedDevice?.[0]?.devices_assigned_date
                      const ownershipType = device.ownership_type || 'purchased'
                      return (
                        <tr key={device.id}>
                          <td className='ps-4'>
                            <div className='d-flex flex-column'>
                              <span className='fw-bold text-dark text-nowrap'>{device?.Device?.name || 'Unknown'}</span>
                              {device?.Device?.model_no && (
                                <span className='text-muted fs-7'>{device.Device.model_no}</span>
                              )}
                              {/* Show IMEI on mobile under device name */}
                              <span className='text-muted fs-7 d-md-none'>{device?.Device?.imei || 'N/A'}</span>
                            </div>
                          </td>
                          <td className='d-none d-md-table-cell'>
                            <span className='text-dark'>{device?.Device?.imei || 'N/A'}</span>
                          </td>
                          <td>
                            <span className={`badge ${
                              ownershipType === 'rental' ? 'bg-info' :
                              ownershipType === 'purchased' ? 'bg-success' : 'bg-warning text-dark'
                            }`}>
                              {ownershipType.charAt(0).toUpperCase() + ownershipType.slice(1)}
                            </span>
                          </td>
                          <td>
                            {patient ? (
                              <div className='d-flex flex-column'>
                                <span className='text-dark'>{patient.display_identifier || patient.email || patient.full_name || 'Unknown'}</span>
                                {/* Show patient assigned date on mobile */}
                                {patientAssignedDate && (
                                  <span className='text-muted fs-7 d-lg-none'>{formatBillingCycleDateToUser(patientAssignedDate, 'MM/DD/YYYY')}</span>
                                )}
                              </div>
                            ) : (
                              <span className='text-muted'>—</span>
                            )}
                          </td>
                          <td className='d-none d-lg-table-cell'>
                            {patientAssignedDate ? (
                              <span className='text-muted'>{formatBillingCycleDateToUser(patientAssignedDate, 'MM/DD/YYYY')}</span>
                            ) : (
                              <span className='text-muted'>—</span>
                            )}
                          </td>
                          <td className='d-none d-lg-table-cell'>
                            <span className='text-muted'>
                              {device?.allocated_at ? formatUTCToUserDate(device.allocated_at) : 'N/A'}
                            </span>
                          </td>
                          <td className='text-end pe-4'>
                            <button
                              className='btn btn-sm btn-light-danger'
                              onClick={() => unassignDevice(device.id)}
                              disabled={unassigningDeviceId === device.id}
                            >
                              {unassigningDeviceId === device.id ? '...' : 'Unassign'}
                            </button>
                          </td>
                        </tr>
                      )
                    })
                  ) : (
                    <tr>
                      <td colSpan={7} className='text-center text-muted py-5'>
                        No devices match the selected filters.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          ) : (
            <div className='p-9'>
              <p className='text-muted'>No devices assigned.</p>
            </div>
          )}
        </div>
      </div>
      {/* Modal Assign Device */}
      {isAssignBulkDeviceModalOpen && (
        <AssignBulkDevicesDoctorModal
          isOpen={isAssignBulkDeviceModalOpen}
          onClose={() => setIsAssignBulkDeviceModalOpen(false)}
          availableDevices={availableDevices}
          handleAssign={assignAvailableDevices}
          isAssigning={isAssigningDevices}
        />
      )}
    </>
  )
}

export default DoctorProfileDevicesPage
